import { createPinia } from 'pinia'
import App from './App.vue'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import { setupHyperDX } from '@/plugins/hyperdx'
import 'virtual:uno.css'
import '@/design-tokens/tokens.css'
// 系统样式作为默认，如果接口返回 theme_css 会被覆盖
import '@/assets/styles/theme.scss'
import '@/assets/styles/global.scss'

import router from './router'
import i18n from './i18n'
import { getUrlParams, getApiOrigin } from '@/utils/common'
import { fetchAndProcessTheme } from '@/utils/theme'

setupHyperDX()

// 暗色加载：调用方可用 `?loadingBg=<color>` 在接口主题返回前就把页面背景钉成目标暗色。
// `--bg-page-bg-color` 同时驱动 .page-container 背景与 Element Plus 的 v-loading 遮罩（见 global.scss），
// 因此设好它即可让「主题拉取 + 数据加载」整个窗口从第一帧就是暗的，而非等接口下发后才变暗。
// 接口返回后 fetchAndProcessTheme 会用真实下发值覆盖此处（站点为准，自动校正色差）。
//
// 双重校验：① 字符白名单挡掉 ;{} 及 var()/url() 等注入/外联引用；② CSS.supports 确认确为
// 浏览器可解析的颜色字面量。后者避免「过了校验却渲染无效」的假安全 —— 如 loadingBg=garbage、
// 非法 hex 长度等都会在此被拒，只有真正能上色的值才写入。
function isValidLoadingColor(v: unknown): v is string {
  return (
    typeof v === 'string' &&
    v !== '' &&
    /^[#a-zA-Z0-9.,%()\s]+$/.test(v) &&
    !/\b(?:var|url)\s*\(/i.test(v) &&
    typeof CSS !== 'undefined' &&
    CSS.supports('color', v)
  )
}

// 记住传过的值：页面间均为整页跳转（window.location.href = `/xxx/${location.search}`），靠透传
// query 携带 loadingBg。一旦某次跳转丢了该参数，回退到本会话此前记住的值，避免某一页突然闪浅。
// 优先级：本次 URL 显式值 > sessionStorage 记住值。显式值同时刷新存储（允许中途换色）。
// 用 sessionStorage：仅同源同标签会话内有效，iframe 整页重载可跨；跨会话/新标签自动失效，不会污染下次。
const LOADING_BG_KEY = 'ticket:loadingBg'
const urlLoadingBgRaw = getUrlParams(window.location.href).loadingBg
const urlLoadingBg = typeof urlLoadingBgRaw === 'string' ? urlLoadingBgRaw.trim() : ''

let resolvedLoadingBg = ''
if (isValidLoadingColor(urlLoadingBg)) {
  resolvedLoadingBg = urlLoadingBg
  try {
    sessionStorage.setItem(LOADING_BG_KEY, urlLoadingBg)
  } catch (_e) {
    // 隐私模式/禁用 storage 时静默：不影响本页生效，仅无法跨页记住
  }
} else {
  try {
    const stored = sessionStorage.getItem(LOADING_BG_KEY)
    if (isValidLoadingColor(stored)) {
      resolvedLoadingBg = stored
    }
  } catch (_e) {
    // 读取失败静默
  }
}

if (resolvedLoadingBg) {
  document.documentElement.style.setProperty('--bg-page-bg-color', resolvedLoadingBg)
}

// 揭示页面：#app 默认隐藏（见 global.scss 的 `#app { opacity: 0 }`），主题应用完成/失败后加 `theme-ready` 揭示。
// 隐藏由渲染阻塞 CSS 在首帧生效，这里只负责「就绪后揭示」，故不存在 JS 加类前的闪烁窗口。
const rootEl = document.documentElement
let appRevealed = false
const revealApp = (): void => {
  if (appRevealed) return
  appRevealed = true
  rootEl.classList.add('theme-ready')
}

// 拉取并应用接口主题；temp-ticket / review 页面不走主题接口，直接 resolve。
async function applyApiTheme(): Promise<void> {
  if (
    window.location.pathname.indexOf('/temp-ticket/') !== -1 ||
    window.location.pathname.indexOf('/review/') !== -1
  ) {
    return
  }
  const query = getUrlParams(window.location.href)
  const host = getApiOrigin()
  const apiUrl = `${host}/theme?language=${query.language}&lang=${query.lang}`
  const css = await fetchAndProcessTheme(apiUrl)

  if (css && css.trim()) {
    // 接口返回的 theme_css 不为空时，注入到系统样式之后以覆盖默认（浅色）token
    const style = document.createElement('style')
    style.type = 'text/css'
    style.id = 'api-theme-style'
    style.textContent = css
    document.head.appendChild(style)
  }
  // theme_css 为空则保留构建时已静态包含的系统（浅色）样式，无需额外处理
}

// 主题应用完成（或失败）即揭示页面；兜底超时防止接口慢/挂起导致长时间空白。
const THEME_REVEAL_TIMEOUT = 2500
applyApiTheme()
  .catch(() => {
    // 主题接口异常已在 fetchAndProcessTheme 内部上报，此处仅防止 unhandled rejection
  })
  .finally(revealApp)
setTimeout(revealApp, THEME_REVEAL_TIMEOUT)

const app = createApp(App)
const pinia = createPinia()

// 先注册所有 app-level 插件，确保 setActivePinia 完成后任何 useXxxStore() 都能取到活跃实例
app.use(pinia)
app.use(router)
app.use(i18n)
app.use(VueReCaptcha, {
  siteKey: import.meta.env.VITE_RECAPTCHA_SITEKEY,
  loaderOptions: {
    autoHideBadge: true
  }
})

// 根据 URL 参数设置语言；useSessionStore() 必须在 app.use(pinia) 之后调用
const query = getUrlParams(window.location.href)
const langParam = query?.lang
type ValidLang = 'en' | 'zh' | 'ja' | 'fr' | 'de' | 'es'
const validLangs: ValidLang[] = ['en', 'zh', 'ja', 'fr', 'de', 'es']

if (langParam && validLangs.includes(langParam as ValidLang)) {
  i18n.global.locale.value = langParam as ValidLang
  const sessionStore = useSessionStore()
  sessionStore.setLang(langParam as ValidLang)
}

app.mount('#app')
